import { FolderIcon } from 'lucide-react';

import {
  MENTION_ROWS_PER_SOURCE,
  type MentionEntry,
} from '@/ui/chat/composer/mention-entry.js';

import type { SharePointSearchHit } from '@/lib/api/sharepoint.js';

import { sharePointPlace } from './sharepoint-place.js';

/**
 * SharePoint files as rows of the composer's `@` popup.
 *
 * There is nothing to filter here: the hits ARE the answer to what the user typed, since the service ran
 * Microsoft Search across everything the workspace's connection reaches. Two things still happen on the
 * way to a row. Folders drop — search answers with them, and a folder is not a file a message can carry.
 * And every row shows the place it was found in, for the reason `sharePointPlace` exists: a search that
 * spans a tenant turns a bare file name into a row nobody can tell from the next one.
 *
 * The row is identified by the PAIR `drive_id` + `item_id`, which is what identifies a file in SharePoint —
 * an `item_id` repeats across libraries, and two rows sharing a key would be one row on screen. The pipe
 * is safe as the join: Graph's ids here are base64url, base32 and GUID text, none of which use it.
 */
export function sharePointMentionEntries(
  hits: SharePointSearchHit[],
  select: (hit: SharePointSearchHit) => void,
): MentionEntry[] {
  return hits
    .filter((hit) => !hit.isFolder)
    .slice(0, MENTION_ROWS_PER_SOURCE)
    .map((hit) => {
      const place = sharePointPlace(hit.webUrl);
      return {
        key: `sharepoint:${hit.driveId}|${hit.itemId}`,
        kind: 'sharepoint' as const,
        icon: FolderIcon,
        label: hit.name,
        detail: place,
        accessibleName:
          place === ''
            ? `SharePoint file ${hit.name}`
            : `SharePoint file ${hit.name}, in ${place}`,
        select: () => select(hit),
      };
    });
}
